import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import logger from "@/lib/logger";
import { useAuth } from "@/context/AuthContext";

const UnreadContext = createContext({ channels: {}, dms: {}, total: 0, refresh: () => {}, markRead: () => {} });

export function UnreadProvider({ children }) {
  const { user } = useAuth();
  const [channels, setChannels] = useState({});
  const [dms, setDms] = useState({});

  const refresh = useCallback(async () => {
    if (!user || user.role === false || user === false) return;
    try {
      const { data } = await api.get("/chat/unread");
      setChannels(data.channels || {});
      setDms(data.dms || {});
    } catch (err) {
      // Best-effort polling — log at debug level only to avoid spamming on network blips
      logger.debug("Unread refresh failed:", err?.message || err);
    }
  }, [user]);

  useEffect(() => {
    if (!user || user === false) return;
    refresh();
    const id = setInterval(refresh, 15000);
    return () => clearInterval(id);
  }, [user, refresh]);

  const markRead = useCallback(
    async (scope_type, scope_id) => {
      try {
        await api.post("/chat/read", { scope_type, scope_id });
        if (scope_type === "channel") {
          setChannels((m) => ({ ...m, [scope_id]: 0 }));
        } else {
          setDms((m) => ({ ...m, [scope_id]: 0 }));
        }
      } catch (err) {
        logger.warn("Mark read failed:", err?.message || err);
      }
    },
    []
  );

  const total = useMemo(
    () => Object.values(channels).reduce((a, b) => a + (b || 0), 0) + Object.values(dms).reduce((a, b) => a + (b || 0), 0),
    [channels, dms]
  );

  const value = useMemo(() => ({ channels, dms, total, refresh, markRead }), [channels, dms, total, refresh, markRead]);
  return <UnreadContext.Provider value={value}>{children}</UnreadContext.Provider>;
}

export const useUnread = () => useContext(UnreadContext);
